import { type FormEvent, type ReactElement, useEffect, useState } from "react";
import { HTTPError } from "ky";

import {
  type Asset,
  createAsset,
  deleteAsset,
  listAssets,
} from "@/api/assets";
import { Btn2 } from "@/components/hifi/Btn2";
import { Card } from "@/components/hifi/Card";
import { IconBtn } from "@/components/hifi/IconBtn";
import { Pill } from "@/components/hifi/Pill";
import { ShellLayout } from "@/components/shell/ShellLayout";

const DEFAULT_CRITICALITY = 3;
const MIN_CRITICALITY = 1;
const MAX_CRITICALITY = 5;

type DraftAsset = {
  name: string;
  asset_type: string;
  category: string;
  criticality: number;
};

const INITIAL_DRAFT: DraftAsset = {
  name: "",
  asset_type: "FACILITY",
  category: "SITE",
  criticality: DEFAULT_CRITICALITY,
};

function critVariant(
  c: number,
): "ok" | "accent" | "warn" | "bad" | "default" {
  if (c >= 5) return "bad";
  if (c === 4) return "warn";
  if (c === 3) return "accent";
  if (c === 2) return "ok";
  return "default";
}

export function AssetsPage(): ReactElement {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [draft, setDraft] = useState<DraftAsset>(INITIAL_DRAFT);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  async function refresh(): Promise<void> {
    setLoading(true);
    try {
      setAssets(await listAssets({ limit: 200 }));
    } catch (err) {
      setError(
        err instanceof HTTPError
          ? `Load failed (${String(err.response.status)})`
          : "Load failed",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleCreate(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await createAsset(draft);
      setDraft(INITIAL_DRAFT);
      setShowForm(false);
      await refresh();
    } catch (err) {
      setError(
        err instanceof HTTPError
          ? `Create failed (${String(err.response.status)})`
          : "Create failed",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id: string): Promise<void> {
    if (!window.confirm("Delete this asset?")) return;
    try {
      await deleteAsset(id);
      await refresh();
    } catch (err) {
      setError(
        err instanceof HTTPError
          ? `Delete failed (${String(err.response.status)})`
          : "Delete failed",
      );
    }
  }

  return (
    <ShellLayout
      active="assets"
      topbar={{
        crumbs: ["Workspace", "Assets"],
        title: "Assets",
        subtitle: `${String(assets.length)} tenant-scoped`,
        right: (
          <Btn2
            variant="accent"
            size="sm"
            icon="plus"
            onClick={() => {
              setShowForm((v) => !v);
            }}
          >
            {showForm ? "Cancel" : "New asset"}
          </Btn2>
        ),
      }}
    >
      <div className="space-y-5 p-5">
        {error && (
          <div
            role="alert"
            className="rounded-2 border border-bad-bg bg-bad-bg/60 px-3 py-2 text-[12px] text-bad"
          >
            {error}
          </div>
        )}

        {showForm && (
          <Card>
            <div className="border-b border-n-150 px-4 py-2.5">
              <div className="text-[13px] font-semibold text-n-900">
                New asset
              </div>
            </div>
            <form
              onSubmit={(e) => {
                void handleCreate(e);
              }}
              className="grid grid-cols-1 gap-3 p-4 md:grid-cols-5"
              noValidate
            >
              <label className="block text-[11px] font-medium text-n-600 md:col-span-2">
                Name
                <input
                  value={draft.name}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, name: e.target.value }));
                  }}
                  required
                  minLength={1}
                  maxLength={200}
                  className="mt-1 block w-full rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
                />
              </label>
              <label className="block text-[11px] font-medium text-n-600">
                Asset type
                <input
                  value={draft.asset_type}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, asset_type: e.target.value }));
                  }}
                  required
                  className="mt-1 block w-full rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
                />
              </label>
              <label className="block text-[11px] font-medium text-n-600">
                Category
                <input
                  value={draft.category}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, category: e.target.value }));
                  }}
                  required
                  className="mt-1 block w-full rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
                />
              </label>
              <label className="block text-[11px] font-medium text-n-600">
                Criticality (1–5)
                <input
                  type="number"
                  value={draft.criticality}
                  onChange={(e) => {
                    setDraft((d) => ({
                      ...d,
                      criticality: Number(e.target.value),
                    }));
                  }}
                  min={MIN_CRITICALITY}
                  max={MAX_CRITICALITY}
                  className="mt-1 block w-full rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
                />
              </label>
              <div className="md:col-span-5">
                <Btn2 variant="accent" type="submit" disabled={submitting}>
                  {submitting ? "Creating…" : "Create asset"}
                </Btn2>
              </div>
            </form>
          </Card>
        )}

        <Card>
          {loading ? (
            <p className="p-4 text-[13px] text-n-500">Loading…</p>
          ) : assets.length === 0 ? (
            <p className="p-4 text-[13px] text-n-500">
              No assets yet. Create one above.
            </p>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-n-150 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.3px] text-n-500">
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Category</th>
                  <th className="px-4 py-2">Criticality</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right" />
                </tr>
              </thead>
              <tbody>
                {assets.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b border-n-100 last:border-0 hover:bg-n-50"
                  >
                    <td className="px-4 py-2.5 font-medium text-n-900">
                      {a.name}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-n-700">
                      {a.asset_type}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-n-700">
                      {a.category}
                    </td>
                    <td className="px-4 py-2.5">
                      <Pill variant={critVariant(a.criticality)}>
                        C{a.criticality}
                      </Pill>
                    </td>
                    <td className="px-4 py-2.5">
                      <Pill variant="outline">{a.status}</Pill>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <IconBtn
                        name="x"
                        size="sm"
                        label="Delete"
                        onClick={() => {
                          void handleDelete(a.id);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </ShellLayout>
  );
}
